// Dashboard onboarding — step model + completion predicates.
// Ported from the "Dashboard Onboarding" design prototype (onboarding/data.jsx)
// but reading real domain fields off Redux `state.app`.

// Utils
import { isEmpty } from "../../../../../utils";

// Constants
import * as tabs from "../../../../../constants/generalBranchSettingsTabs.constants";
import * as menuKeys from "../../../../../constants/menu-items/menu-items.constants";

// Branch sub-steps open the active branch's settings page, deep-linked to the
// relevant tab via ?tab=<KEY> (GeneralBranchSettings reads it from the URL on mount).
// `setup` additionally tells the tab which card to ping (see .tf-setup-ping).
const branchSettingsRoute = (activeBranch, tab, setup) => {
  if (!activeBranch?.id) return "/branches";
  const query = new URLSearchParams();
  if (tab) query.set("tab", tab);
  if (setup) query.set("setup", setup);
  const search = query.toString();
  return `/branch-settings/${activeBranch.id}${search ? `?${search}` : ""}`;
};

export const REQUIRED_STEPS = [
  {
    id: "verify",
    num: 1,
    glyph: "shield",
    title: "Verify your business",
    desc:
      "Confirm your business details so drivers and fleets know your shop is legit.",
    cta: "Verify business",
    route: "Business Profile",
    // ?setup=proof tells BusinessProfile to ping the proof dropzone — same
    // deep-link trick the branch sub-steps use with ?tab=<KEY>.
    to: () => "/business-profile?setup=proof"
  },
  {
    id: "branch",
    num: 2,
    glyph: "building",
    title: "Create your first branch",
    desc: "Add the shop location where you'll take trucks in for service.",
    cta: "Add a branch",
    route: "Manage Branches",
    to: () => "/create-branch"
  },
  {
    id: "types",
    num: 3,
    glyph: "types",
    title: "Add service types to the branch",
    desc:
      "Tell drivers what this branch handles — Truck Shop, Road Assistance, Towing and more.",
    cta: "Add service types",
    route: "Branch · Service Type & Capacity",
    to: a => branchSettingsRoute(a, tabs.SERVICE_TYPE_AND_CAPACITY)
  },
  {
    id: "schedule",
    num: 4,
    glyph: "calendar",
    title: "Set the branch schedule",
    desc: "Define working days and hours so jobs can be booked into open slots.",
    cta: "Set work days",
    route: "Branch · Manage Schedule",
    to: a => branchSettingsRoute(a, tabs.MANAGE_SCHEDULE)
  },
  {
    id: "equipment",
    num: 5,
    glyph: "truck",
    title: "Add supported equipment",
    desc:
      "List the truck makes & models your shop services — required before the branch can go live.",
    cta: "Add manufacturers",
    route: "Branch · General Settings",
    to: a => branchSettingsRoute(a, tabs.GENERAL_SETTINGS, "equipment")
  },
  {
    id: "plan",
    num: 6,
    glyph: "card",
    title: "Choose a plan & set up billing",
    desc:
      "Pick a subscription and add a payment method — this unlocks taking your branch live.",
    cta: "Choose a plan",
    route: "Subscription Plans",
    to: () => "/subscription-plans"
  },
  {
    id: "live",
    num: 7,
    glyph: "globe",
    title: "Go live",
    desc:
      "Flip the visibility switch so drivers and fleets can find this branch and book jobs.",
    cta: "Publish branch",
    route: "Branch · General Settings",
    to: a => branchSettingsRoute(a, tabs.GENERAL_SETTINGS, "live"),
    checks: [{ k: "active", label: "Branch is visible in the app" }]
  }
];

export const RECOMMENDED_STEPS = [
  {
    id: "menu",
    glyph: "card",
    title: "Build your service menu",
    desc: "Add priced services so customers can see what this branch offers.",
    cta: "Add services",
    to: a => branchSettingsRoute(a, tabs.SERVICE_MENU)
  },
  {
    id: "staff",
    glyph: "staff",
    title: "Invite your staff",
    desc: "Add technicians and managers who work at this branch.",
    cta: "Invite staff",
    to: a => branchSettingsRoute(a, tabs.STAFF_MANAGEMENT)
  },
  {
    id: "roles",
    glyph: "key",
    title: "Set roles & permissions",
    desc: "Control what each team member can see and do.",
    cta: "Configure roles",
    to: a => branchSettingsRoute(a, tabs.ROLES_AND_PERMISSIONS)
  }
];

// Completion predicates. `s` = the OnboardingStatusResponse payload from
// GET /branches/onboarding-status (authoritative, computed server-side). It carries
// the verify/plan signals (via s.dashboardData) plus the per-branch flags & counts.
export const isDone = (id, s = {}) => {
  switch (id) {
    case "verify":
      return s.dashboardData?.verificationStatus === "verified";
    case "plan":
      return !isEmpty(s.dashboardData?.subscriptionPlanName);
    case "branch":
      return s.hasBranch === true;
    case "types":
      return s.hasServiceTypes === true;
    case "schedule":
      return s.hasWorkSchedule === true;
    case "live":
      // Branch visibility switch is on. (Building the menu is now recommended.)
      return s.isActive === true;
    case "menu":
      return s.serviceMenuCount > 0;
    case "equipment":
      return s.hasEquipment === true;
    case "staff":
      return s.staffCount > 0;
    case "roles":
      return s.rolesCount > 0;
    default:
      return false;
  }
};

export const verificationPending = (s = {}) =>
  s.dashboardData?.verificationStatus === "pending";

export const requiredDoneCount = s =>
  REQUIRED_STEPS.filter(step => isDone(step.id, s)).length;

export const allRequiredDone = s =>
  requiredDoneCount(s) === REQUIRED_STEPS.length;

// --- Onboarding gating (side menu + routes) ---------------------------------
// A step is "reached" if done, or the first not-done one (mirrors the
// statusKind sequence in buildOnboardingStatus).
export const reachedStepIds = (s = {}) => {
  const active = REQUIRED_STEPS.findIndex(step => !isDone(step.id, s));
  return new Set(
    REQUIRED_STEPS.filter(
      (step, i) => isDone(step.id, s) || i === active
    ).map(step => step.id)
  );
};

// null = always allowed while onboarding; key absent = hidden/blocked until
// onboarding completes. Values are the step id that unlocks the surface.
export const MENU_STEP_GATE = {
  [menuKeys.DASHBOARD]: null,
  [menuKeys.BUSINESS_PROFILE]: "verify",
  [menuKeys.MANAGE_BRANCHES]: "branch"
};

// Keys are the exact route `path` strings from router.config.js.
export const ROUTE_STEP_GATE = {
  "/": null,
  "/account-settings": null,
  "/subscription-plans": null,
  "/business-profile": "verify",
  "/branches": "branch",
  "/create-branch": "branch",
  "/branches/:id": "branch",
  "/branch-settings/:id": "branch"
};

// Dev-only presets so every visual state can be forced via ?onboarding=<key>
// without mutating backend data. Shapes mirror the OnboardingStatusResponse payload.
export const PRESETS = {
  fresh: {
    dashboardData: {
      verificationStatus: "not_verified",
      subscriptionPlanName: ""
    },
    hasBranch: false,
    isActive: false,
    hasServiceTypes: false,
    hasWorkSchedule: false,
    hasEquipment: false,
    serviceMenuCount: 0,
    staffCount: 0,
    rolesCount: 0
  },
  pending: {
    dashboardData: { verificationStatus: "pending", subscriptionPlanName: "" },
    hasBranch: false,
    isActive: false,
    hasServiceTypes: false,
    hasWorkSchedule: false,
    hasEquipment: false,
    serviceMenuCount: 0,
    staffCount: 0,
    rolesCount: 0
  },
  progress: {
    // Mid-flow under the new order: plan (step 6) not picked yet.
    dashboardData: {
      verificationStatus: "verified",
      subscriptionPlanName: ""
    },
    hasBranch: true,
    isActive: false,
    hasServiceTypes: true,
    hasWorkSchedule: false,
    hasEquipment: false,
    serviceMenuCount: 0,
    staffCount: 0,
    rolesCount: 0
  },
  complete: {
    dashboardData: {
      verificationStatus: "verified",
      subscriptionPlanName: "Pro Monthly"
    },
    hasBranch: true,
    isActive: true,
    hasServiceTypes: true,
    hasWorkSchedule: true,
    hasEquipment: true,
    serviceMenuCount: 2,
    staffCount: 1,
    rolesCount: 1
  }
};
